import { faDryer, faSitemap } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '~/components/ui/HoverTooltip';
import { ASSET_DRIVEN_TEMPLATE_TOOLTIP } from '~/constants/template';

export type TemplateBehavior = 'static' | 'asset_rules';

/**
 * GraphQL enum values for TemplateBehavior
 */
type GraphQLTemplateBehavior = 'Static' | 'AssetRules';

/**
 * Converts GraphQL enum value to internal TemplateBehavior format
 */
const fromGraphQLBehavior = (behavior: unknown): TemplateBehavior => {
  if (behavior === 'AssetRules') return 'asset_rules';
  if (behavior === 'Static') return 'static';
  return 'static'; // default fallback
};

/**
 * Converts internal TemplateBehavior format to GraphQL enum value
 */
export const toGraphQLBehavior = (
  behavior: TemplateBehavior | null | undefined
): GraphQLTemplateBehavior => {
  if (behavior === 'asset_rules') return 'AssetRules';
  return 'Static'; // default to 'Static' for 'static' or undefined/null
};

/**
 * Type guard to safely access behavior field on templates
 * Converts GraphQL enum values ('Static', 'AssetRules') to internal format ('static', 'asset_rules')
 */
export const getTemplateBehavior = (
  template: { behavior?: unknown } | null | undefined
): TemplateBehavior => {
  if (!template) return 'static';
  const behavior = template.behavior;
  return fromGraphQLBehavior(behavior);
};

type Props = {
  /** Size variant for the indicator */
  size?: 'sm' | 'md';
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * AssetDrivenTemplateIndicator - Visual indicator for asset-driven templates
 *
 * Displays an icon badge that signals a template creates tasks based on
 * selected assets rather than having a fixed set of tasks.
 *
 * Used in:
 * - Template picker (UseTemplateButton) to differentiate template types
 * - Template list views
 */
export const AssetDrivenTemplateIndicator = ({
  size = 'sm',
  showTooltip = true,
  className,
}: Props) => {
  const { t } = useTranslation();

  const tooltipText = t(
    'job:assetDrivenTemplate.tooltip',
    ASSET_DRIVEN_TEMPLATE_TOOLTIP
  );

  const indicator = (
    <span
      className={classNames(
        'inline-flex items-center justify-center rounded-full bg-shade-10 text-brand',
        {
          'h-5 w-5': size === 'sm',
          'h-6 w-6': size === 'md',
        },
        className
      )}
      aria-label={tooltipText}
    >
      <FontAwesomeIcon
        icon={faSitemap}
        className={classNames({
          'h-2.5 w-2.5': size === 'sm',
          'h-3 w-3': size === 'md',
        })}
      />
    </span>
  );

  if (!showTooltip) {
    return indicator;
  }

  return <HoverTooltip content={tooltipText}>{indicator}</HoverTooltip>;
};

/**
 * Helper to check if a template is asset-driven
 */
export const isAssetDrivenTemplate = (
  behavior: TemplateBehavior | null | undefined
): boolean => {
  return behavior === 'asset_rules';
};

/**
 * Badge variant for inline text display
 */
export const AssetDrivenBadge = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const tooltipText = t(
    'job:assetDrivenTemplate.tooltip',
    ASSET_DRIVEN_TEMPLATE_TOOLTIP
  );

  const badge = (
    <span
      className={classNames(
        'ml-auto inline-flex items-center gap-1 rounded-full bg-shade-10 px-2 py-0.5 text-xs font-medium text-brand',
        className
      )}
    >
      <FontAwesomeIcon icon={faDryer} className='h-2.5 w-2.5' />
      {t('job:assetDrivenTemplate.badge', 'Asset-driven')}
    </span>
  );

  return <HoverTooltip content={tooltipText}>{badge}</HoverTooltip>;
};
